import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { api } from '@/global/services/api'
import { cloneDeep } from '@/global/utils/utils'
import { FormDataBuilder } from '@/global/services/helpers/formDataBuilder'
import { showDetailsAction, deleteAction, activationAction } from '@/global/components/table/helpers/tableActions'

const TABLE_COMPONENT_KEY = 'sims'
export default {
  data () {
    return {
      informationDialogConfig: {
        openDialog: false,
        message: '',
        title: '',
        positiveBtnText: '',
        negativeBtnText: this.$t('base.cancel'),
        callbackFn: () => {}
      },
      manageSimDialogConfig: {
        openDialog: false,
        positiveButtonText: this.$t('base.save'),
        negativeButtonText: this.$t('base.cancel'),
        title: '',
        edit: false,
        callback: () => this.onSaveSimCard()
      },
      simChangeCompanyDialogConfig: {
        openDialog: false,
        positiveButtonText: this.$t('base.save'),
        negativeButtonText: this.$t('base.cancel'),
        title: '',
        callback: () => this.onChangeCompanyCallback()
      },
      simsImportFile: null,
      simCardFormData: {},
      simChangeCompanyFormData: {},
      simCardFormValidationErrors: {},
      simChangeCompanyFormValidationErrors: {},
      showSaveSimLoader: false,
      showSimChangeCompanyLoader: false,
      mainTableKey: TABLE_COMPONENT_KEY
    }
  },

  computed: {
    tableRowActions () {
      return [
        {
          ...showDetailsAction(this.$t, (event) => this.showEdit(event)),
          visible: (event) => {
            return !event?.data?.master_app && !!this.permissions?.canUpdate
          }
        },
        {
          ...activationAction(this.$t, (event) => this.onSimActivation(event), (event) => !event?.data?.deactivated_at),
          visible: (event) => {
            return !event?.data?.master_app && !!this.permissions?.canActivate
          }
        },
        {
          ...deleteAction(this.$t, (event) => this.onSimDelete(event)),
          visible: (event) => {
            return !event?.data?.master_app && !!this.permissions?.canDelete
          }
        },
        {
          label: this.$t('base.change_company'),
          icon: 'mdi-factory',
          visible: !!this.permissions?.canChangeCompany,
          click: (event) => this.onChangeCompany(event)
        }
      ]
    }
  },

  methods: {
    showEdit (event) {
      const { data } = event || {}
      const { iccid } = data || {}
      // cloneDeep for disabling changes on source object by reference
      const clonedData = cloneDeep(data)
      // edit data
      const editData = {}

      // map edit data
      editData.tariff_id = clonedData?.tariff?.id
      editData.iccid = clonedData?.iccid
      editData.voice_number = clonedData?.voice_number
      editData.roaming_enabled = clonedData?.roaming_enabled
      editData.id = clonedData?.id

      // set form data
      this.simCardFormData = editData

      this.$set(this.manageSimDialogConfig, 'title', this.$t('base/sim.sim') + (iccid ? ' - ' + iccid : ''))
      this.$set(this.manageSimDialogConfig, 'edit', true)
      this.$set(this.manageSimDialogConfig, 'openDialog', true)
    },

    onSimActivation (event) {
      const {
        deactivated_at: deactivatedAt,
        id,
        iccid
      } = event?.data || {}

      if (!id) {
        return
      }

      const title = !deactivatedAt ? this.$t('base/sim.deactivate') : this.$t('base/sim.activate')
      this.$set(this.informationDialogConfig, 'title', title + (iccid ? ' - ' + iccid : ''))
      this.$set(this.informationDialogConfig, 'positiveBtnText', !deactivatedAt ? this.$t('base.deactivate') : this.$t('base.activate'))
      this.$set(this.informationDialogConfig, 'message', !deactivatedAt ? this.$t('base/sim.confirm_deactivation') : this.$t('base/sim.confirm_activation'))
      this.$set(this.informationDialogConfig, 'callbackFn', () => this.handleSimActivation(event))
      this.$set(this.informationDialogConfig, 'openDialog', true)
    },

    async handleSimActivation (event) {
      try {
        const { id, deactivated_at: deactivatedAt } = event?.data || {}
        const { fetchData } = registeredTableHandlers?.[this.mainTableKey] || {}
        await api('base').patch(`sim/${id}/${deactivatedAt ? 'activate' : 'deactivate'}`)

        this.$set(this.informationDialogConfig, 'openDialog', false)
        if (typeof fetchData === 'function') {
          fetchData()
        }
      }
      catch (exception) {
        console.error(exception)
      }
    },

    onSimDelete (event) {
      const {
        id,
        iccid
      } = event?.data || {}

      if (!id) {
        return
      }

      const title = this.$t('base/sim.delete')
      this.$set(this.informationDialogConfig, 'title', title + (iccid ? ' - ' + iccid : ''))
      this.$set(this.informationDialogConfig, 'positiveBtnText', this.$t('base.save'))
      this.$set(this.informationDialogConfig, 'message', this.$t('base/sim.confirm_delete'))
      this.$set(this.informationDialogConfig, 'callbackFn', () => this.handleSimDeletion(event))
      this.$set(this.informationDialogConfig, 'openDialog', true)
    },

    async handleSimDeletion (event) {
      try {
        const { id } = event?.data || {}
        const { fetchData } = registeredTableHandlers?.[this.mainTableKey] || {}

        await api('base').delete(`sim/${id}`)

        this.$set(this.informationDialogConfig, 'openDialog', false)
        if (typeof fetchData === 'function') {
          fetchData()
        }
      }
      catch (exception) {
        console.error(exception)
      }
    },

    onChangeCompany (event) {
      const { data } = event || {}
      const { iccid } = data || {}
      const clonedData = cloneDeep(data)
      this.simChangeCompanyFormData = {
        id: clonedData?.id
      }
      this.$set(this.simChangeCompanyDialogConfig, 'title', this.$t('base.change_company_title') + (iccid ? ' - ' + iccid : ''))
      this.$set(this.simChangeCompanyDialogConfig, 'openDialog', true)
    },

    async onChangeCompanyCallback () {
      try {
        this.showSimChangeCompanyLoader = true
        if (!this.simChangeCompanyFormData?.id) {
          return
        }

        const formDataBuilder = new FormDataBuilder()
        const formData = formDataBuilder.build(this.simChangeCompanyFormData)
        formData.append('_method', 'PATCH')

        await api('base').post(`sim/${this.simChangeCompanyFormData.id}/change-company`, formData)

        const { fetchData } = registeredTableHandlers[TABLE_COMPONENT_KEY] || {}
        this.closeSimChangeCompanyDialog()
        // refresh table after success import
        if (typeof fetchData === 'function') {
          fetchData()
        }
      }
      catch (exception) {
        this.simChangeCompanyFormValidationErrors = exception?.response?.data?.errors || {}
        console.error(exception)
      }
      finally {
        this.showSimChangeCompanyLoader = false
      }
    },

    closeManageSimDialog () {
      this.$set(this.manageSimDialogConfig, 'openDialog', false)
      this.simCardFormData = {}
      this.simCardFormValidationErrors = {}
    },

    closeSimChangeCompanyDialog () {
      this.$set(this.simChangeCompanyDialogConfig, 'openDialog', false)
    },

    onClearSimValidationErrors (key) {
      if (this.simCardFormValidationErrors?.[key]) {
        this.$delete(this.simCardFormValidationErrors, key)
      }
    },

    onClearSimChangeCompanyValidationErrors (key) {
      if (this.simChangeCompanyFormValidationErrors?.[key]) {
        this.$delete(this.simChangeCompanyFormValidationErrors, key)
      }
    },

    async onSaveSimCard () {
      try {
        this.showSaveSimLoader = true
        if (this.manageSimDialogConfig.edit && !this.simCardFormData?.id) {
          return
        }

        const formDataBuilder = new FormDataBuilder()
        const formData = formDataBuilder.build(this.simCardFormData)
        let route = 'sim'

        if (this.manageSimDialogConfig.edit) {
          formData.append('_method', 'PATCH')
          route += `/${this.simCardFormData?.id}`
        }

        await api('base').post(route, formData)

        const { fetchData } = registeredTableHandlers[TABLE_COMPONENT_KEY] || {}
        this.closeManageSimDialog()
        // refresh table after success import
        if (typeof fetchData === 'function') {
          fetchData()
        }
      }
      catch (exception) {
        this.simCardFormValidationErrors = exception?.response?.data?.errors || {}
        console.error(exception)
      }
      finally {
        this.showSaveSimLoader = false
      }
    }
  }
}
